import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import CosmicBackground from '../components/CosmicBackground';
import PageTransition from '../components/PageTransition';

const Services = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const services = [
    {
      title: "🌟 Kundli Dosh and Yog Analysis",
      image: "https://images.unsplash.com/photo-1602981256888-244edc1f444f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxuZWJ1bGF8ZW58MHx8fHB1cnBsZXwxNzU3Nzc3MDk5fDA&ixlib=rb-4.1.0&q=85",
      description: "Comprehensive cosmic analysis of planetary positions, doshas, and divine yogas in your sacred birth chart to understand karmic patterns and celestial influences.",
      color: "from-purple-500 to-indigo-600",
      symbol: "🪐"
    },
    {
      title: "📚 Sacred Education Analysis",
      image: "https://images.unsplash.com/photo-1602979082099-7971376fce79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxuZWJ1bGF8ZW58MHx8fHB1cnBsZXwxNzU3Nzc3MDk5fDA&ixlib=rb-4.1.0&q=85",
      description: "Divine educational path guidance based on celestial influences on learning, memory, wisdom, and intellectual capabilities for cosmic academic success.",
      color: "from-blue-500 to-purple-600",
      symbol: "📜"
    },
    {
      title: "💖 Marriage & Soul Connection Analysis",
      image: "https://images.unsplash.com/photo-1718315631556-5e9edc04015e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxuZWJ1bGF8ZW58MHx8fHB1cnBsZXwxNzU3Nzc3MDk5fDA&ixlib=rb-4.1.0&q=85",
      description: "Sacred compatibility analysis, divine relationship timing, and celestial guidance for harmonious soul partnerships and mystical marital bliss.",
      color: "from-pink-500 to-rose-600",
      symbol: "💫"
    },
    {
      title: "🌙 Career & Wealth Cosmic Guidance",
      image: "https://images.pexels.com/photos/4069292/pexels-photo-4069292.jpeg",
      description: "Divine professional guidance, cosmic career timing, celestial business prospects, and wealth manifestation potential based on stellar influences.",
      color: "from-yellow-500 to-orange-600",
      symbol: "✨"
    },
    {
      title: "🔮 Complete Horoscope Divination",
      image: "https://images.pexels.com/photos/6806400/pexels-photo-6806400.jpeg",
      description: "Complete cosmic life analysis covering all celestial aspects including health, relationships, career, finances, and spiritual awakening journey.",
      color: "from-indigo-500 to-purple-600",
      symbol: "🌌"
    },
    {
      title: "🌠 Cosmic Destiny Reading",
      image: "https://images.pexels.com/photos/7278845/pexels-photo-7278845.jpeg",
      description: "Profound cosmic destiny analysis revealing your soul's purpose, life mission, and divine path through the celestial wisdom of the stars.",
      color: "from-violet-500 to-pink-600",
      symbol: "🌟"
    }
  ];

  return (
    <PageTransition>
      <CosmicBackground variant="services">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="min-h-screen pt-20"
      >
        <div className="max-w-6xl mx-auto px-4 py-12">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-300 via-pink-300 to-purple-300 bg-clip-text text-transparent leading-tight py-2">
              ✨ Sacred Astrology Services ✨
            </h1>
            
            <p className="text-xl text-gray-200 max-w-4xl mx-auto leading-relaxed">
              Discover cosmic insights and divine guidance through our comprehensive range of mystical Vedic astrology services
            </p>

            {/* Simple symbols */}
            <div className="flex justify-center items-center space-x-8 mt-8">
              {['🌙', '⭐', '🔮', '✨', '🌟'].map((symbol, index) => (
                <span key={index} className="text-3xl opacity-80">
                  {symbol}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                whileHover={{ y: -3, scale: 1.02 }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/20 hover:border-purple-400/50 transition-all duration-300"
              >
                {/* Image Section */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  
                  {/* Service Symbol */}
                  <div className="absolute top-4 right-4 text-2xl bg-black/30 backdrop-blur-sm rounded-full p-2">
                    {service.symbol}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-4 text-white">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-300 leading-relaxed mb-6 text-sm">
                    {service.description}
                  </p>
                  
                  {/* Learn More button removed as requested */}
                </div>
              </motion.div>
            ))}
          </div>

          {/* What We Do Best Section - Simplified */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-2xl p-8 border border-white/20"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 bg-gradient-to-r from-yellow-300 to-yellow-200 bg-clip-text text-transparent leading-tight py-1">
              ✨ What We Do Best ✨
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "🌙 Sacred Relationships Counselling",
                  content: "For some souls, their cosmic connection with family or divine partner flows with celestial harmony, while others experience karmic challenges. Through ancient wisdom and stellar guidance, we illuminate your path to harmonious relationships.",
                  icon: "💫"
                },
                {
                  title: "🔮 Divine Human Nature Counselling",
                  content: "Did you know that your thoughts, actions, and very essence are profoundly influenced by cosmic stellar energies? When a gifted cosmic reader reveals profound truths about your soul's nature, it awakens divine surprise.",
                  icon: "🌟"
                },
                {
                  title: "📚 Sacred Education & Career Counselling",
                  content: "The realm of learning and education flows from the cosmic functioning of your divine mind. Your mystical interest in knowledge and destined career path can be illuminated through horoscope analysis.",
                  icon: "✨"
                },
                {
                  title: "🎨 Divine Hobbies & Sacred Interests Counselling",
                  content: "Often, souls remain unaware of their divine hobbies and mystical interests. Your sacred birth chart speaks of hidden celestial talents and divine flair for arts, sports, and mystical activities.",
                  icon: "🎭"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.4 }}
                  className="bg-gradient-to-br from-white/5 to-white/10 p-6 rounded-xl border border-white/10"
                >
                  <div className="flex items-center mb-4">
                    <span className="text-3xl mr-3">{item.icon}</span>
                    <h3 className="text-xl font-bold text-purple-200">
                      {item.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed">
                    {item.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </CosmicBackground>
    </PageTransition>
  );
};

export default Services;
